// client/src/components/pages/LexiStar/DispatchBoard/TruckRail.tsx (ST-00250)
//
// The rail of trucks: available ones first (draggable onto an order card), then those in the
// workshop or broken down (shown so the planner sees why a truck is missing; not draggable). Each
// chip: the plate, its current trip or workshop state, its loads on this board and its 90-day
// average per load. Three ways to allocate without a mouse: tap a chip to select it (the cards
// then offer "Allocate here"), or press Enter / the "Allocate to order…" button for a menu of the
// day's open orders (arrow keys, Enter, Escape). Dropping an allocation chip onto the rail
// releases it.

import { useEffect, useId, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { FaCarCrash, FaWrench, FaTruck, FaEllipsisV, FaCheck } from 'react-icons/fa';
import type { BoardAllocation, BoardOrder, BoardTruck } from '../../../../types/lexistar-dispatch-board';
import { DRAG_TYPE, decodeDrag, encodeDrag, fmtTonnes, sortTrucks, truckName, truckStatusText } from './boardLogic';

export interface TruckRailProps {
  trucks: BoardTruck[];
  orders: BoardOrder[];
  canManage: boolean;
  selectedAssetId: string | null;
  onSelect: (assetId: string | null) => void;
  onAllocate: (truck: BoardTruck, order: BoardOrder) => void;
  /** An allocation chip dropped back on the rail. */
  onReleaseDrop: (orderId: string, allocationId: string) => void;
  busy: boolean;
  /** Which allocations are on which truck (for "n loads" when an order is being edited). */
  allocationsOf?: (assetId: string) => BoardAllocation[];
}

function AllocateMenu({
  truck,
  orders,
  onPick,
  onClose,
}: {
  truck: BoardTruck;
  orders: BoardOrder[];
  onPick: (order: BoardOrder) => void;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const ref = useRef<HTMLUListElement>(null);
  const open = orders.filter((o) => o.status === 'open');
  useEffect(() => {
    ref.current?.focus();
  }, []);
  const onKey = (e: KeyboardEvent<HTMLUListElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex((i) => Math.min(open.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const o = open[index];
      if (o) onPick(o);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };
  return (
    <ul
      ref={ref}
      role="menu"
      tabIndex={-1}
      aria-label={`Allocate ${truckName(truck)} to order`}
      onKeyDown={onKey}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onClose();
      }}
      className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-lg border border-slate-200 bg-surface p-1 shadow-xl focus:outline-none"
    >
      {open.length === 0 && (
        <li role="none" className="px-3 py-2 text-xs text-slate-500">
          No open orders on this board
        </li>
      )}
      {open.map((o, i) => (
        <li key={o.id} role="none">
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            aria-current={i === index || undefined}
            onMouseEnter={() => setIndex(i)}
            onClick={() => onPick(o)}
            className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm ${i === index ? 'bg-primary-50 text-primary-800' : 'text-slate-800 hover:bg-slate-50'}`}
          >
            <span className="truncate">
              <span className="font-semibold">{o.order_ref}</span>
              {o.customer ? ` · ${o.customer}` : ''}
              {o.material ? ` · ${o.material}` : ''}
            </span>
            <span className="shrink-0 text-xs text-slate-500 tabular-nums">{o.absorption.pct == null ? '' : `${o.absorption.pct}%`}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function TruckChip({
  truck,
  orders,
  canManage,
  selected,
  onSelect,
  onAllocate,
  busy,
}: {
  truck: BoardTruck;
  orders: BoardOrder[];
  canManage: boolean;
  selected: boolean;
  onSelect: (id: string | null) => void;
  onAllocate: (truck: BoardTruck, order: BoardOrder) => void;
  busy: boolean;
}) {
  const [menu, setMenu] = useState(false);
  const menuId = useId();
  const available = truck.availability === 'available';
  const draggable = canManage && available && !busy;
  const Icon = truck.availability === 'breakdown' ? FaCarCrash : truck.availability === 'planned_maintenance' ? FaWrench : FaTruck;
  const onDragStart = (e: DragEvent<HTMLElement>) => {
    e.dataTransfer.setData(DRAG_TYPE, encodeDrag({ kind: 'truck', asset_id: truck.asset_id }));
    e.dataTransfer.effectAllowed = 'copy';
  };
  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!canManage || !available) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setMenu(true);
    }
  };
  return (
    <li className="relative">
      <div
        draggable={draggable}
        onDragStart={onDragStart}
        data-testid={`truck-${truck.asset_id}`}
        data-availability={truck.availability}
        className={`flex items-center gap-2 rounded-xl border px-2.5 py-2 ${
          selected ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/30' : available ? 'border-slate-200 bg-surface' : 'border-slate-200 bg-slate-50 opacity-70'
        } ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
      >
        <button
          type="button"
          onClick={() => (canManage && available ? onSelect(selected ? null : truck.asset_id) : undefined)}
          onKeyDown={onKey}
          aria-pressed={canManage && available ? selected : undefined}
          aria-label={`${truckName(truck)}: ${truckStatusText(truck)}${available && canManage ? (selected ? '. Selected: choose an order to allocate it to' : '. Press Enter to allocate to an order') : ''}`}
          disabled={!canManage || !available}
          className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-default"
        >
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
              truck.availability === 'breakdown'
                ? 'bg-[color:var(--as-breakdown-tint)] text-[color:var(--as-breakdown-text)]'
                : truck.availability === 'planned_maintenance'
                  ? 'bg-[color:var(--as-planned-maintenance-tint)] text-[color:var(--as-planned-maintenance-text)]'
                  : selected
                    ? 'bg-primary-500 text-white'
                    : 'bg-slate-100 text-slate-600'
            }`}
            aria-hidden="true"
          >
            {selected ? <FaCheck size={12} /> : <Icon size={13} />}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-slate-900">{truckName(truck)}</span>
            <span className="block truncate text-[11px] text-slate-500">{truckStatusText(truck)}</span>
          </span>
        </button>
        <span className="shrink-0 text-right text-[11px] leading-tight text-slate-500 tabular-nums" title="Average net tonnes per load, last 90 days">
          {truck.avg_tonnes != null ? fmtTonnes(truck.avg_tonnes, 1) : '—'}
          <span className="block text-[10px]">{truck.avg_loads ? `${truck.avg_loads} loads` : 'no history'}</span>
        </span>
        {canManage && available && (
          <button
            type="button"
            onClick={() => setMenu((m) => !m)}
            aria-haspopup="menu"
            aria-expanded={menu}
            aria-controls={menu ? menuId : undefined}
            aria-label={`Allocate ${truckName(truck)} to order…`}
            title="Allocate to order…"
            disabled={busy}
            className="touch-target-sm shrink-0 rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
          >
            <FaEllipsisV size={11} />
          </button>
        )}
      </div>
      {menu && (
        <div id={menuId}>
          <AllocateMenu
            truck={truck}
            orders={orders}
            onPick={(o) => {
              setMenu(false);
              onAllocate(truck, o);
            }}
            onClose={() => setMenu(false)}
          />
        </div>
      )}
    </li>
  );
}

export default function TruckRail({ trucks, orders, canManage, selectedAssetId, onSelect, onAllocate, onReleaseDrop, busy }: TruckRailProps) {
  const [over, setOver] = useState(false);
  const sorted = sortTrucks(trucks);
  const available = sorted.filter((t) => t.availability === 'available');
  const unavailable = sorted.filter((t) => t.availability !== 'available');
  const onDragOver = (e: DragEvent<HTMLElement>) => {
    if (!canManage || !Array.from(e.dataTransfer.types).includes(DRAG_TYPE)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!over) setOver(true);
  };
  const onDrop = (e: DragEvent<HTMLElement>) => {
    setOver(false);
    const payload = decodeDrag(e.dataTransfer.getData(DRAG_TYPE));
    if (payload?.kind === 'allocation') {
      e.preventDefault();
      onReleaseDrop(payload.order_id, payload.allocation_id);
    }
  };
  return (
    <aside
      aria-label="Trucks"
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={`rounded-2xl border bg-surface p-3 shadow-sm ${over ? 'border-primary-500 ring-4 ring-primary-500/20' : 'border-slate-200'}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Trucks <span className="text-slate-400">({available.length} available)</span>
        </h2>
        {selectedAssetId && (
          <button type="button" onClick={() => onSelect(null)} className="text-xs font-medium text-primary-700 hover:underline">
            Clear selection
          </button>
        )}
      </div>
      {canManage && <p className="mb-2 text-[11px] text-slate-500">Drag a truck onto an order, or select it and choose the order. Drop a chip back here to release it.</p>}
      {trucks.length === 0 && <p className="text-sm text-slate-500">No trucks in your asset register.</p>}
      <ul className="grid gap-1.5 sm:grid-cols-2 xl:grid-cols-1" aria-label="Available trucks">
        {available.map((t) => (
          <TruckChip key={t.asset_id} truck={t} orders={orders} canManage={canManage} selected={selectedAssetId === t.asset_id} onSelect={onSelect} onAllocate={onAllocate} busy={busy} />
        ))}
      </ul>
      {unavailable.length > 0 && (
        <details className="mt-3" open={available.length === 0}>
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-slate-500">
            Not available <span className="text-slate-400">({unavailable.length})</span>
          </summary>
          <ul className="mt-1.5 grid gap-1.5 sm:grid-cols-2 xl:grid-cols-1" aria-label="Trucks not available">
            {unavailable.map((t) => (
              <TruckChip key={t.asset_id} truck={t} orders={orders} canManage={canManage} selected={false} onSelect={onSelect} onAllocate={onAllocate} busy={busy} />
            ))}
          </ul>
        </details>
      )}
    </aside>
  );
}
